/**
 * StandingsPage — an association's season standings board, browsable by any
 * signed-in user.
 *
 * Pick ANY association and year and see every contestant's running total:
 * MANAGED winnings, recomputed from live standings at rodeos HighCall runs,
 * plus ADDITIONAL winnings a delegate typed in for the ones it doesn't (see
 * docs/season-winnings.md). Nothing here is the editing surface — that is the
 * delegate's Season Roster — so the board carries no notes.
 *
 * Two numbers lead each row. `#` is the plain 1..N sequence of the rows AS
 * DISPLAYED, so a reader knows how long the list is and where they are; it
 * renumbers under a sort or a search. `Place` is the competition rank by
 * total under the current filter (ties share, the next place skips), assigned
 * by the server, and never moves.
 *
 * The event type filter re-totals the numbers a type can own — managed,
 * additional (that type's delegate-entered external results), entered,
 * placed, total and place — server-side. It never changes who is listed. A
 * legacy lump adjustment carries no event, so it drops out under a filter.
 *
 * When the viewer is on the board, a compact card row above the table shows
 * their own numbers; when they aren't, nothing is shown there. Any row expands
 * to the contestant's per-rodeo ledger, fetched only on expand so the board
 * load itself never carries breakdowns.
 *
 * The season sums EVERY payout scope an event configures — each go-round plus
 * the average — while the dashboard's Event History credits the average only,
 * so these totals can legitimately read higher. The note under the board
 * explains that rather than leaving an apparent contradiction.
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import { Trophy, Search, ChevronDown, ChevronRight } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { YearSelect } from '@/components/common/YearSelect';
import { useToast } from '@/contexts/ToastContext';
import { useDebounce } from '@/hooks/useDebounce';
import { useAuthStore } from '@/state_management_and_stores/auth.store';
import {
  useSeasonAssociations,
  useSeasonStandings,
  type SeasonStandingsRow,
} from '@/api/extensions/useSeasonWinningsOps';
import { formatCurrency, getOrdinal } from '@/utils/formatters';
import { StandingsRowDetail } from './StandingsRowDetail';

/** Remembers the association across visits; a season is followed over months. */
const ORG_STORAGE_KEY = 'standings-org';
/** Radix Select can't take an empty-string value, so "every event type" is this. */
const ALL_EVENT_TYPES = 'all';

function readStoredOrg(): string | undefined {
  try {
    return localStorage.getItem(ORG_STORAGE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

function storeOrg(orgId: string) {
  try {
    localStorage.setItem(ORG_STORAGE_KEY, orgId);
  } catch {
    // no-op: persistence is best-effort
  }
}

type SortKey =
  | 'place'
  | 'name'
  | 'entered'
  | 'placed'
  | 'rodeos'
  | 'managed'
  | 'additional'
  | 'total';
type SortOrder = 'asc' | 'desc';

function sortValue(row: SeasonStandingsRow, key: SortKey): number {
  switch (key) {
    case 'place':
      return row.place;
    case 'entered':
      return row.events_entered;
    case 'placed':
      return row.events_placed;
    case 'rodeos':
      return row.rodeos_attended;
    case 'managed':
      return row.managed_winnings;
    case 'additional':
      return row.additional_winnings;
    case 'total':
      return row.total_winnings;
    default:
      return 0;
  }
}

function compareRows(a: SeasonStandingsRow, b: SeasonStandingsRow, key: SortKey, order: SortOrder) {
  const direction = order === 'asc' ? 1 : -1;
  const byName = (a.display_name ?? '').localeCompare(b.display_name ?? '');
  if (key === 'name') return byName * direction;
  const delta = sortValue(a, key) - sortValue(b, key);
  // Ties fall back to name so the list order is stable between renders.
  return delta !== 0 ? delta * direction : byName;
}

function matchesSearch(row: SeasonStandingsRow, term: string): boolean {
  if (!term) return true;
  const haystack = `${row.display_name ?? ''} ${row.member_number ?? ''}`.toLowerCase();
  return haystack.includes(term);
}

export default function StandingsPage() {
  const { showError } = useToast();
  const viewerId = useAuthStore((s) => s.profile?.id);
  const [orgId, setOrgId] = useState<string | undefined>(readStoredOrg);
  const [year, setYear] = useState<number | undefined>(undefined);
  const [eventTypeId, setEventTypeId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<SortKey>('total');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const debouncedSearch = useDebounce(search, 300);

  // Every real association, most active first — the board is browsable, so
  // the picker is not scoped to the viewer's memberships.
  const {
    data: associations,
    isLoading: associationsLoading,
    error: associationsError,
  } = useSeasonAssociations();
  const associationOptions = useMemo(() => associations ?? [], [associations]);

  // Default to the most active association once the list arrives, and recover
  // if a remembered one is no longer on it.
  useEffect(() => {
    if (associationOptions.length === 0) return;
    if (orgId && associationOptions.some((o) => o.organization_id === orgId)) return;
    setOrgId(associationOptions[0].organization_id);
  }, [associationOptions, orgId]);

  const selected = associationOptions.find((o) => o.organization_id === orgId);

  // The year must be one the association offers; default to its newest.
  useEffect(() => {
    if (!selected) return;
    if (year && selected.years.includes(year)) return;
    setYear(selected.years[0]);
  }, [selected, year]);

  const { data: board, isLoading: boardLoading, error: boardError } = useSeasonStandings(
    orgId,
    year,
    eventTypeId,
  );
  // With `keepPreviousData` a filter change re-totals in place; a season change
  // must not show the previous season's board under the new heading.
  const currentBoard =
    board && board.organization_id === orgId && board.season_year === year ? board : undefined;

  useEffect(() => {
    const err = (associationsError ?? boardError) as Error | null;
    if (!err) return;
    showError('Could not load standings', err.message);
  }, [associationsError, boardError, showError]);

  // A different season is a different set of event types and contestants, so
  // the filter and the open row start over with it.
  const resetSeasonState = () => {
    setEventTypeId(null);
    setExpandedId(null);
  };

  const handleOrgChange = (next: string) => {
    setOrgId(next);
    storeOrg(next);
    resetSeasonState();
  };

  const handleYearChange = (next: number | undefined) => {
    if (!next) return;
    setYear(next);
    resetSeasonState();
  };

  const handleSort = (key: SortKey) => {
    if (sortBy === key) setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    else {
      setSortBy(key);
      // Names read A→Z; every number reads biggest (or best place) first.
      setSortOrder(key === 'name' || key === 'place' ? 'asc' : 'desc');
    }
  };

  const rows = useMemo(() => {
    const term = debouncedSearch.trim().toLowerCase();
    const filtered = (currentBoard?.contestants ?? []).filter((r) => matchesSearch(r, term));
    return [...filtered].sort((a, b) => compareRows(a, b, sortBy, sortOrder));
  }, [currentBoard?.contestants, debouncedSearch, sortBy, sortOrder]);
  // Summed over what's on screen — with a search active the footer answers
  // "what do these contestants add up to", not "what did the season pay".
  const shownTotal = useMemo(() => rows.reduce((sum, r) => sum + r.total_winnings, 0), [rows]);

  const you = viewerId
    ? currentBoard?.contestants.find((r) => r.user_profile_id === viewerId)
    : undefined;

  const rodeoCount = currentBoard?.rodeo_count ?? 0;
  const filtered = eventTypeId != null;
  const eventTypeName = currentBoard?.event_types.find((t) => t.id === eventTypeId)?.name;
  const orgLabel =
    selected?.organization_name ?? currentBoard?.organization_name ?? 'this association';
  const loading = associationsLoading || (boardLoading && !currentBoard && !!orgId && !!year);

  return (
    <div className="space-y-6">
      {/* Header + selectors */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Trophy className="h-6 w-6 text-primary" />
          <h1 className="text-xl font-bold">Standings</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={orgId ?? ''} onValueChange={handleOrgChange}>
            <SelectTrigger className="w-full sm:w-[260px]" aria-label="Association">
              <SelectValue placeholder="Select an association" />
            </SelectTrigger>
            <SelectContent>
              {associationOptions.map((org) => (
                <SelectItem key={org.organization_id} value={org.organization_id}>
                  {org.organization_name ?? 'Association'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* Rendered once the association's seasons are known, so Radix never
              flips from uncontrolled to controlled. */}
          {selected && year && (
            <YearSelect
              value={year}
              onChange={handleYearChange}
              years={selected.years}
              aria-label="Season year"
            />
          )}
          {currentBoard && currentBoard.event_types.length > 0 && (
            <Select
              value={eventTypeId ?? ALL_EVENT_TYPES}
              onValueChange={(next) => {
                setEventTypeId(next === ALL_EVENT_TYPES ? null : next);
              }}
            >
              <SelectTrigger className="w-full sm:w-[220px]" aria-label="Event type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_EVENT_TYPES}>All events</SelectItem>
                {currentBoard.event_types.map((type) => (
                  <SelectItem key={type.id} value={type.id}>
                    {type.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {associationOptions.length === 0 && !associationsLoading ? (
        <EmptyState
          icon={Trophy}
          title="No associations yet"
          description="Standings appear here once an association has run a rodeo."
        />
      ) : loading ? (
        <div className="space-y-3">
          <Skeleton className="h-9 w-full sm:w-72" />
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : currentBoard ? (
        <>
          {currentBoard.contestants.length === 0 ? (
            <EmptyState
              icon={Trophy}
              title={`No results in ${currentBoard.season_year}`}
              description={`${orgLabel} has no standings for this season yet. Money appears here once an event has been scored.`}
            />
          ) : (
            <>
              {you && (
                <YouCards row={you} rodeoCount={rodeoCount} />
              )}

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-muted-foreground">
                  {currentBoard.season_year} season at {orgLabel}
                  {rodeoCount > 0 ? ` · ${rodeoCount} ${rodeoCount === 1 ? 'rodeo' : 'rodeos'}` : ''}
                  {filtered && eventTypeName ? ` · ${eventTypeName}` : ''}
                </p>
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    className="h-9 pl-9"
                    placeholder="Name or member number"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    aria-label="Search contestants"
                  />
                </div>
              </div>

              <div className="overflow-x-auto rounded-lg border">
                <Table className="min-w-[860px]" aria-label="Season standings">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12 text-right">#</TableHead>
                      <SortableHead label="Place" sortKey="place" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} className="w-16" />
                      <SortableHead label="Contestant" sortKey="name" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} align="left" />
                      <SortableHead label="Entered" sortKey="entered" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                      <SortableHead label="Placed" sortKey="placed" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                      <SortableHead label="Rodeos" sortKey="rodeos" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                      <SortableHead label="Managed" sortKey="managed" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                      <SortableHead label="Additional" sortKey="additional" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                      <SortableHead label="Total" sortKey="total" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                      <TableHead>Finals</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((row, index) => {
                      const isYou = !!viewerId && row.user_profile_id === viewerId;
                      const isExpanded = expandedId === row.user_profile_id;
                      const toggle = () =>
                        setExpandedId((prev) => (prev === row.user_profile_id ? null : row.user_profile_id));
                      return (
                        <Fragment key={row.user_profile_id}>
                          <TableRow
                            data-testid={isYou ? 'standings-own-row' : undefined}
                            className={`cursor-pointer ${isYou ? 'bg-primary/10 hover:bg-primary/15' : ''}`}
                            tabIndex={0}
                            aria-expanded={isExpanded}
                            onClick={toggle}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                toggle();
                              }
                            }}
                          >
                            <TableCell className="text-right tabular-nums text-muted-foreground">
                              {index + 1}
                            </TableCell>
                            <TableCell className="text-right tabular-nums font-medium">
                              {row.place}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-start gap-2">
                                <span
                                  aria-hidden
                                  className="mt-0.5 shrink-0 text-muted-foreground"
                                  title={isExpanded ? 'Collapse results' : 'Expand results'}
                                >
                                  {isExpanded ? (
                                    <ChevronDown className="h-4 w-4" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4" />
                                  )}
                                </span>
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className={isYou ? 'font-semibold' : 'font-medium'}>
                                      {row.display_name || 'Unnamed contestant'}
                                    </span>
                                    {isYou && (
                                      <Badge className="bg-primary/15 text-primary border-primary/30">
                                        You
                                      </Badge>
                                    )}
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    {row.is_member ? row.member_number || 'Member' : 'Non-member'}
                                  </div>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-right tabular-nums">{row.events_entered}</TableCell>
                            <TableCell className="text-right tabular-nums">{row.events_placed}</TableCell>
                            <TableCell className="text-right tabular-nums">
                              {row.rodeos_attended} / {rodeoCount}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {formatCurrency(row.managed_winnings)}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {formatCurrency(row.additional_winnings)}
                            </TableCell>
                            <TableCell className="text-right tabular-nums font-bold">
                              {formatCurrency(row.total_winnings)}
                            </TableCell>
                            <TableCell>
                              {row.finals_eligible ? (
                                <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30">
                                  Eligible
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground">--</span>
                              )}
                            </TableCell>
                          </TableRow>
                          {isExpanded && orgId && year && (
                            <TableRow className="bg-muted/30 hover:bg-muted/30">
                              <TableCell colSpan={10} className="px-6 py-3">
                                <StandingsRowDetail
                                  orgId={orgId}
                                  year={year}
                                  userId={row.user_profile_id}
                                  eventTypeId={eventTypeId}
                                />
                              </TableCell>
                            </TableRow>
                          )}
                        </Fragment>
                      );
                    })}
                    {rows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={10} className="py-6 text-center text-muted-foreground">
                          No contestants match your search.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                <div className="flex items-center justify-between gap-4 border-t bg-muted/40 px-4 py-2 text-sm font-medium">
                  <span>
                    {rows.length} {rows.length === 1 ? 'contestant' : 'contestants'}
                  </span>
                  <span className="tabular-nums">{formatCurrency(shownTotal)}</span>
                </div>
              </div>

              <p className="text-xs text-muted-foreground">
                Totals include go-round money as well as average payouts, so they can read
                higher than a contestant's dashboard Event History.
                {filtered
                  ? ' While an event is selected, Additional counts only outside results entered under that event; a legacy season lump is left out.'
                  : ''}
              </p>
            </>
          )}
        </>
      ) : null}
    </div>
  );
}

/**
 * Sortable column header. Click toggles direction on the active column and
 * otherwise starts from the natural reading order the page picks.
 */
function SortableHead({
  label,
  sortKey,
  sortBy,
  sortOrder,
  onSort,
  align = 'right',
  className = '',
}: {
  label: string;
  sortKey: SortKey;
  sortBy: SortKey;
  sortOrder: SortOrder;
  onSort: (key: SortKey) => void;
  align?: 'left' | 'right';
  className?: string;
}) {
  const isActive = sortBy === sortKey;
  return (
    <TableHead
      aria-sort={isActive ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={`${align === 'right' ? 'text-right' : 'text-left'} ${className}`}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1 select-none transition-colors hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 ${
          isActive ? 'text-foreground' : ''
        }`}
      >
        {label}
        {isActive ? <span aria-hidden="true">{sortOrder === 'asc' ? '▲' : '▼'}</span> : null}
      </button>
    </TableHead>
  );
}

/**
 * The viewer's own numbers, above the table, when they are on the board. The
 * same figures as their row — one place to look without scrolling for the
 * "You" chip. Additional follows the filter: that event's outside results.
 */
function YouCards({ row, rodeoCount }: { row: SeasonStandingsRow; rodeoCount: number }) {
  const tiles: Array<{ label: string; value: string; highlight?: boolean }> = [
    { label: 'Place', value: getOrdinal(row.place) },
    { label: 'Total winnings', value: formatCurrency(row.total_winnings), highlight: true },
    { label: 'Managed', value: formatCurrency(row.managed_winnings) },
  ];
  tiles.push({ label: 'Additional', value: formatCurrency(row.additional_winnings) });
  tiles.push(
    { label: 'Entered / placed', value: `${row.events_entered} / ${row.events_placed}` },
    { label: 'Rodeos attended', value: `${row.rodeos_attended} / ${rodeoCount}` },
  );
  return (
    <section
      aria-label="Your standing"
      data-testid="standings-you-cards"
      className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6"
    >
      {tiles.map((tile) => (
        <div
          key={tile.label}
          className="rounded-xl border border-primary/20 bg-primary/5 px-3 py-2"
        >
          <div className="text-xs text-muted-foreground">{tile.label}</div>
          <div
            className={`text-lg font-bold tabular-nums tracking-tight ${
              tile.highlight ? 'text-green-600' : ''
            }`}
          >
            {tile.value}
          </div>
        </div>
      ))}
    </section>
  );
}
